import { useState } from "react";
import { ApiValidationError } from "./api";
import type { EditorMetadata, TransitionType } from "./types";
import type { EditorMutations } from "./useEditorMutations";

/**
 * Create a transition by choosing its statuses.
 *
 * The graph's drag-to-connect does the same thing, but only between statuses already on it — a
 * definition cannot grow a new status that way, and dragging is not a keyboard gesture. This form
 * is the complete route.
 */
export function AddTransitionForm({
    metadata,
    mutations,
}: {
    metadata: EditorMetadata;
    mutations: EditorMutations;
}) {
    const [fromStatus, setFromStatus] = useState("");
    const [toStatus, setToStatus] = useState("");
    const [type, setType] = useState<TransitionType>(
        (metadata.transitionTypes[0] as TransitionType) ?? "AUTO",
    );

    const add = mutations.addTransition;
    const fieldErrors =
        add.error instanceof ApiValidationError ? add.error.fieldErrors : {};

    // The server enforces this too; saying it here avoids a pointless round trip.
    const isSelfTransition = Boolean(fromStatus) && fromStatus === toStatus;

    const statuses = [...metadata.statuses].sort((a, b) => a.rank - b.rank);

    return (
        <section className="aer-wf-section">
            <h3>Add a transition</h3>

            <div className="aer-wf-fields aer-wf-fields--row">
                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-from">From</label>
                    <select
                        id="aer-wf-from"
                        value={fromStatus}
                        onChange={(event) => setFromStatus(event.target.value)}
                    >
                        <option value="">— choose —</option>
                        {statuses.map((status) => (
                            <option key={status.value} value={status.value}>
                                {status.value}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-to">To</label>
                    <select
                        id="aer-wf-to"
                        value={toStatus}
                        onChange={(event) => setToStatus(event.target.value)}
                    >
                        <option value="">— choose —</option>
                        {statuses.map((status) => (
                            <option key={status.value} value={status.value}>
                                {status.value}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-new-type">Type</label>
                    <select
                        id="aer-wf-new-type"
                        value={type}
                        onChange={(event) =>
                            setType(event.target.value as TransitionType)
                        }
                    >
                        {metadata.transitionTypes.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    type="button"
                    className="aer-wf-button"
                    disabled={
                        !fromStatus ||
                        !toStatus ||
                        isSelfTransition ||
                        add.isLoading
                    }
                    onClick={() =>
                        add.mutate(
                            {
                                fromStatus: fromStatus,
                                toStatus: toStatus,
                                transitionType: type,
                            },
                            {
                                onSuccess: () => {
                                    setFromStatus("");
                                    setToStatus("");
                                },
                            },
                        )
                    }
                >
                    {add.isLoading ? "Adding…" : "Add transition"}
                </button>
            </div>

            {isSelfTransition && (
                <p className="aer-wf-field__error" role="alert">
                    A transition's from and to status cannot be the same.
                </p>
            )}

            {Object.entries(fieldErrors).map(([field, messages]) => (
                <p className="aer-wf-field__error" role="alert" key={field}>
                    {field === "non_field_errors" ? "" : `${field}: `}
                    {messages.join(" ")}
                </p>
            ))}
        </section>
    );
}
